import type * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'

import type * as models from './models'
import { State } from './state'

/**
 * Core actions for the agent assignment feature
 */
// NOTE: intentionally missing return type so that type can be inferred
export function getActionsConfig() {
  return {
    /**
     * Retrieves a list of users filtered by {@link Filters}
     */
    listUsers: store.actions.request({
      input: store.actions.Data.asIs<{
        filters?: models.User.Filters
      }>(),
      output: store.actions.Data.asIs<{
        readonly users: readonly models.User[]
      }>(),
      reducers: {
        request: ({ state }) => ({
          ...State.setInitial(),
          isEditing: state.isEditing,
        }),
        error: ({ state, data: { error } }) => ({
          ...state,
          ...{
            error,
          },
        }),
        success: ({
          state,
          data: {
            response: { users },
          },
        }) => ({
          ...state,
          ...{
            users,
          },
        }),
      },
    }),
    /**
     * Assigning agent access for user
     */
    assignAgentAccess: store.actions.request({
      input: store.actions.Data.asIs<{
        assignments: readonly ents.core.admin.Access.Assignment[]
      }>(),
      output: store.actions.Data.NONE,
      reducers: {
        request: ({ state }) => ({
          ...state,
          mutationError: undefined,
        }),
        success: ({ state }) => State.reset(state),
        error: ({ state, data: { error } }) => ({
          ...state,
          mutationError: error,
        }),
      },
    }),
    setEditingMode: store.actions.standalone({
      data: store.actions.Data.asIs<{
        isEditing: boolean
      }>(),
      reducer: ({ state, data: { isEditing } }) => ({
        ...state,
        isEditing,
        mutationError: undefined,
      }),
    }),
    setSelectedUserIds: store.actions.standalone({
      data: store.actions.Data.asIs<{
        readonly ids: readonly ents.core.admin.User.Id[]
      }>(),
      reducer: ({ state, data: { ids } }) => ({
        ...state,
        selectedUserIds: ids,
      }),
    }),
    setSelectedAgentIds: store.actions.standalone({
      data: store.actions.Data.asIs<{
        readonly ids: readonly ents.core.agent.Agent.Id[]
      }>(),
      reducer: ({ state, data: { ids } }) => ({
        ...state,
        selectedAgentIds: ids,
      }),
    }),
    setAccess: store.actions.standalone({
      data: store.actions.Data.asIs<{
        readonly access: ents.core.admin.Access
      }>(),
      reducer: ({ state, data: { access } }) => ({
        ...state,
        access,
      }),
    }),
    reset: store.actions.standalone({
      data: store.actions.Data.NONE,
      reducer: ({ state }) => State.reset(state),
    }),
    markUserForUnassignment: store.actions.standalone({
      data: store.actions.Data.asIs<{
        readonly user: models.User | undefined
        readonly agentId?: ents.core.agent.Agent.Id
      }>(),
      reducer: ({ state, data: { user, agentId } }) => ({
        ...state,
        markedAgentIdForUnassignment: agentId,
        markedUserForUnassignment: user,
        mutationError: undefined,
      }),
    }),
    setPageAliases: store.actions.standalone({
      data: store.actions.Data.asIs<{
        readonly pageAliases: Set<string> | undefined
      }>(),
      reducer: ({ state, data: { pageAliases } }) => ({
        ...state,
        pageAliases,
      }),
    }),
    setIsForNonPrebuiltPages: store.actions.standalone({
      data: store.actions.Data.asIs<{
        readonly isForNonPrebuiltPages: boolean | undefined
      }>(),
      reducer: ({ state, data: { isForNonPrebuiltPages } }) => ({
        ...state,
        isForNonPrebuiltPages,
      }),
    }),
  } as const satisfies store.actions.Config.Constraint<State>
}
